import { useMemo, useState } from "react"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import ControlPointIcon from "@mui/icons-material/ControlPoint"
import EditOutlined from "@mui/icons-material/EditOutlined"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import SyncIcon from "@mui/icons-material/Sync"
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined"
import { CellContext, ColumnDef } from "@tanstack/react-table"
import { toast } from "react-toastify"

import { ChipStyled } from "src/shared/components"
import { DataGridMui } from "src/shared/container/data-grid-mui"
import {
  IntegrationProductsDocument,
  useIntegrationProductsQuery,
  usePushIntegrationProductMutation,
  useRemoveIntegrationProductMutation,
  useUnlistIntegrationProductMutation
} from "src/graphql/quark/quark.type"
import { FEATURE_MAX_SALES_QUANTITY, FEATURE_PRODUCT_CREATE } from "../connectorFeatures"
import AddProductModal from "./AddProductModal"
import BulkMapProductsDialog from "./BulkMapProductsDialog"
import ProductSettingsDialog from "./ProductSettingsDialog"

type ProductRow = {
  productId: string
  name: string
  barcode?: string | null
  active: boolean
  externalSku?: string | null
  stockBuffer?: number | null
  maximumSalesQuantity?: number | null
  listingStatus?: string | null
  lastError?: string | null
  externalUrl?: string | null
  lastPushedAt?: string | null
}

type ChipColor = "success" | "info" | "error" | "warning" | "default"

const LISTING_STATUS: Record<string, { label: string; color: ChipColor }> = {
  PENDING_CREATE: { label: "Creating", color: "info" },
  PENDING_UPDATE: { label: "Updating", color: "info" },
  SYNCED: { label: "In sync", color: "success" },
  FAILED: { label: "Failed", color: "error" },
  REMOTE_DELETED: { label: "Deleted on channel", color: "warning" },
  UNLISTING: { label: "Removing", color: "default" }
}

const ListingStatusCell = ({ row }: { row: ProductRow }) => {
  const status = row.listingStatus ? LISTING_STATUS[row.listingStatus] : null
  const failed = row.listingStatus === "FAILED"

  return (
    <Box>
      <ChipStyled
        size="small"
        variant="outlined"
        label={status?.label ?? row.listingStatus ?? "Not pushed"}
        color={status?.color ?? "default"}
      />
      {failed && row.lastError && (
        <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5, maxWidth: 280 }}>
          {row.lastError}
        </Typography>
      )}
    </Box>
  )
}

const ProductsTab = ({
  connectionId,
  integrationName,
  defaultStockBuffer,
  features,
  canEdit
}: {
  connectionId: string
  integrationName: string
  defaultStockBuffer: number | null
  features: string[]
  canEdit: boolean
}) => {
  const [addOpen, setAddOpen] = useState(false)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [editing, setEditing] = useState<ProductRow | null>(null)
  const [unlisting, setUnlisting] = useState<ProductRow | null>(null)

  const createsProducts = features.includes(FEATURE_PRODUCT_CREATE)
  const showMaxSales = features.includes(FEATURE_MAX_SALES_QUANTITY)

  const { data, loading } = useIntegrationProductsQuery({
    variables: { connectionId },
    fetchPolicy: "network-only"
  })

  const refetchQueries = [{ query: IntegrationProductsDocument, variables: { connectionId } }]

  const [removeProduct] = useRemoveIntegrationProductMutation({ refetchQueries })
  const [pushProduct, { loading: pushing }] = usePushIntegrationProductMutation({ refetchQueries })
  const [unlistProduct, { loading: unlistSaving }] = useUnlistIntegrationProductMutation({ refetchQueries })

  const handleRemove = async (row: ProductRow) => {
    try {
      await removeProduct({
        variables: { input: { connectionId, oneObjectId: row.productId } }
      })
      toast.success(`${row.name} removed`)
    } catch (error: any) {
      toast.error(error?.message ?? "Could not remove the product")
    }
  }

  const handlePush = async (row: ProductRow) => {
    try {
      await pushProduct({ variables: { input: { connectionId, productId: row.productId } } })
      toast.success(`${row.name} is going out to ${integrationName}`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not push the product")
    }
  }

  const handleUnlist = async () => {
    if (!unlisting) return
    try {
      await unlistProduct({ variables: { input: { connectionId, productId: unlisting.productId } } })
      toast.success(`${unlisting.name} is being removed from ${integrationName}`)
      setUnlisting(null)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not remove the product")
    }
  }

  const columns: ColumnDef<ProductRow, any>[] = useMemo(
    () => [
      { header: "Product", accessorKey: "name" },
      {
        header: "Barcode",
        accessorKey: "barcode",
        cell: ({ row }) => row.original.barcode ?? "—"
      },
      { header: createsProducts ? "SKU" : "SKU on this integration", accessorKey: "externalSku" },
      createsProducts
        ? {
            header: "Status",
            accessorKey: "listingStatus",
            cell: ({ row }) => <ListingStatusCell row={row.original} />
          }
        : {
            header: "Status",
            accessorKey: "active",
            cell: ({ row }) => (
              <ChipStyled
                size="small"
                variant="outlined"
                label={row.original.active ? "Active" : "Inactive"}
                color={row.original.active ? "success" : "default"}
              />
            )
          },
      {
        header: "Stock buffer",
        accessorKey: "stockBuffer",
        cell: ({ row }) =>
          row.original.stockBuffer != null ? (
            row.original.stockBuffer
          ) : (
            <Typography variant="body2" color="text.secondary">
              {defaultStockBuffer != null ? `Default (${defaultStockBuffer})` : "None"}
            </Typography>
          )
      },
      ...(showMaxSales
        ? [
            {
              header: "Max per order",
              accessorKey: "maximumSalesQuantity",
              cell: ({ row }: CellContext<ProductRow, unknown>) =>
                row.original.maximumSalesQuantity != null ? (
                  row.original.maximumSalesQuantity
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No limit
                  </Typography>
                )
            }
          ]
        : []),
      ...(createsProducts
        ? [
            {
              header: "Last pushed",
              accessorKey: "lastPushedAt",
              cell: ({ row }: CellContext<ProductRow, unknown>) =>
                row.original.lastPushedAt ? (
                  new Date(row.original.lastPushedAt).toLocaleString()
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Never
                  </Typography>
                )
            }
          ]
        : []),
      {
        header: "",
        id: "actions",
        cell: ({ row }) => (
          <Box display="flex">
            {createsProducts && row.original.externalUrl && (
              <Tooltip title={`Open on ${integrationName}`}>
                <IconButton
                  size="small"
                  component="a"
                  href={row.original.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {createsProducts && (
              <Tooltip title="Push now">
                <span>
                  <IconButton
                    size="small"
                    disabled={!canEdit || pushing || row.original.listingStatus === "UNLISTING"}
                    onClick={() => void handlePush(row.original)}
                  >
                    <SyncIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title={showMaxSales ? "Edit stock buffer and max per order" : "Edit stock buffer"}>
              <span>
                <IconButton size="small" disabled={!canEdit} onClick={() => setEditing(row.original)}>
                  <EditOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Remove">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={!canEdit}
                  onClick={() => (createsProducts ? setUnlisting(row.original) : void handleRemove(row.original))}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )
      }
    ],
    [canEdit, defaultStockBuffer, createsProducts, showMaxSales, integrationName, pushing]
  )

  const rows: ProductRow[] = data?.fusionIntegrationProducts ?? []

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
        <Button
          variant="outlined"
          startIcon={<UploadFileOutlined />}
          disabled={!canEdit}
          onClick={() => setBulkOpen(true)}
        >
          Bulk upload
        </Button>
        <Button
          variant="contained"
          startIcon={<ControlPointIcon />}
          disabled={!canEdit}
          onClick={() => setAddOpen(true)}
        >
          Add product
        </Button>
      </Box>

      <DataGridMui
        id="integration-products"
        data={rows}
        columns={columns}
        isLoading={loading}
        paginationMode="client"
        noDataComponent={<Box p={3}>No products are published to this integration yet.</Box>}
      />

      <AddProductModal
        connectionId={connectionId}
        integrationName={integrationName}
        defaultStockBuffer={defaultStockBuffer}
        createsProducts={createsProducts}
        showMaxSales={showMaxSales}
        open={addOpen}
        onClose={() => setAddOpen(false)}
      />

      <BulkMapProductsDialog
        connectionId={connectionId}
        integrationName={integrationName}
        createsProducts={createsProducts}
        showMaxSales={showMaxSales}
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
      />

      <ProductSettingsDialog
        connectionId={connectionId}
        integrationName={integrationName}
        product={editing}
        defaultStockBuffer={defaultStockBuffer}
        showMaxSales={showMaxSales}
        onClose={() => setEditing(null)}
      />

      <Dialog open={!!unlisting} onClose={() => setUnlisting(null)} fullWidth maxWidth="xs">
        <DialogTitle>Remove from {integrationName}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {unlisting?.name} will be archived on {integrationName}, not deleted, and its stock will stop updating
            there. You can add it again later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlisting(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={unlistSaving} onClick={() => void handleUnlist()}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ProductsTab
